import React, { Suspense } from 'react';
import { useSettings } from '../context/SettingsContext';
import HeroCarousel from './HeroCarousel';
import TrustBadges from './TrustBadges';
import BrowseCategories from './BrowseCategories';
import WhyChooseUs from './WhyChooseUs';
import PromotionalBanner from './PromotionalBanner';
import ProductGrid from './ProductGrid';
import FlashDealsSection from './FlashDealsSection';
import TrendingProducts from './TrendingProducts';
import TrustedBrands from './TrustedBrands';
import TestimonialsSection from './TestimonialsSection';
import NewsletterSection from './NewsletterSection';

const PersonalizedSections = React.lazy(() => import('./PersonalizedSections'));

const HomePageSections = ({
  user, featuredProducts, bestSellers, allProducts,
  handleAddToCart, handleWishlist, handleQuickView,
  handleCategorySelect, setActivePage, wishlistItems,
}) => {
  const settings = useSettings();
  const layout = settings.homepageLayout;

  // Get section config helper
  const getSection = (key) => {
    if (!layout?.sections) return { enabled: true, max_items: 6 };
    return layout.sections.find(s => s.key === key) || { enabled: true, max_items: 6 };
  };

  const getMaxItems = (key) => getSection(key).max_items || 6;

  // Get products for a section: use product_ids if admin selected, else fallback
  const getProductsForSection = (key, fallback) => {
    const section = getSection(key);
    if (section.product_ids?.length > 0 && allProducts?.length > 0) {
      // Use admin-selected product IDs in order
      return section.product_ids
        .map(id => allProducts.find(p => p.id === id))
        .filter(Boolean);
    }
    return fallback || [];
  };

  // Build ordered sections
  const sectionComponents = {
    hero: () => <HeroCarousel />,
    trust_badges: () => <TrustBadges />,
    categories: () => (
      <BrowseCategories
        onCategorySelect={handleCategorySelect}
        onViewAll={() => { setActivePage('all-products'); window.scrollTo(0, 0); }}
        maxItems={getMaxItems('categories')}
      />
    ),
    why_choose_us: () => <WhyChooseUs />,
    featured: () => (
      <ProductGrid
        title="Featured Products"
        products={getProductsForSection('featured', featuredProducts)}
        onAddToCart={handleAddToCart}
        onWishlist={handleWishlist}
        onQuickView={handleQuickView}
        limit={getMaxItems('featured')}
        compact
        onViewAll={() => { setActivePage('featured'); window.scrollTo(0, 0); }}
      />
    ),
    promo_banner: () => <PromotionalBanner />,
    best_sellers: () => (
      <ProductGrid
        title="Best Sellers"
        products={getProductsForSection('best_sellers', bestSellers)}
        onAddToCart={handleAddToCart}
        onWishlist={handleWishlist}
        onQuickView={handleQuickView}
        limit={getMaxItems('best_sellers')}
        compact
        onViewAll={() => { setActivePage('best-sellers'); window.scrollTo(0, 0); }}
      />
    ),
    flash_deals: () => <FlashDealsSection onAddToCart={handleAddToCart} onQuickView={handleQuickView} />,
    new_arrivals: () => {
      // Sort all products by created_at desc (newest first) and slice
      const limit = getMaxItems('new_arrivals');
      const sorted = (allProducts || [])
        .slice()
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, limit);
      const picked = getProductsForSection('new_arrivals', sorted);
      if (!picked.length) return null;
      return (
        <ProductGrid
          title="New Arrivals"
          products={picked}
          onAddToCart={handleAddToCart}
          onWishlist={handleWishlist}
          onQuickView={handleQuickView}
          limit={limit}
          compact
          onViewAll={() => { setActivePage('all-products'); window.scrollTo(0, 0); }}
        />
      );
    },
    trending: () => <TrendingProducts onAddToCart={handleAddToCart} onQuickView={handleQuickView} />,
    personalized: () => user ? (
      <div className="bg-gray-50 dark:bg-gray-900 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <Suspense fallback={<div className="h-32 flex items-center justify-center"><div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" /></div>}>
            <PersonalizedSections
              userId={user.id}
              onAddToCart={handleAddToCart}
              onQuickView={handleQuickView}
              onWishlist={handleWishlist}
              wishlistItems={wishlistItems}
            />
          </Suspense>
        </div>
      </div>
    ) : null,
    brands: () => <TrustedBrands />,
    testimonials: () => <TestimonialsSection />,
    newsletter: () => <NewsletterSection />,
  };

  // Get ordered, enabled sections
  const orderedSections = layout?.sections
    ? [...layout.sections].sort((a, b) => a.order - b.order).filter(s => s.enabled)
    : Object.keys(sectionComponents).map((key, i) => ({ key, order: i, enabled: true }));

  return (
    <main id="main-content" role="main">
      {orderedSections.map(section => {
        const Component = sectionComponents[section.key];
        if (!Component) return null;
        return <React.Fragment key={section.key}>{Component()}</React.Fragment>;
      })}
    </main>
  );
};

export default HomePageSections;
